import { IGif } from '@giphy/js-types'
import { isEmpty } from 'lodash'
import React, { PureComponent } from 'react'
import { fetchChannel } from 'shared/api/channels'
import ChannelInfo from 'shared/components/channel-info/info'
import ChannelPageContext, { ChannelType, ChannelPageContextProps } from 'shared/components/context/channel-page'
import { GridType } from 'shared/components/context/grid-type'
import RefreshDataContext, { RefreshChannelOptions } from 'shared/components/context/refresh-data'
import Screensaver from 'shared/components/screensaver/screensaver'
import { VideoPreviewChannelGrid } from 'shared/components/video-preview-grid'
import SharedVideoPaginationCarousel from 'shared/components/video-preview-thumbnail/pagination-carousel'
import { assertIsDefined } from 'shared/types/asserts'
import { IChannel } from 'shared/types/channel'
import { setGTADataLayer } from 'shared/util/analytics'
import { getUrl } from 'shared/util/gif-extract'
import { grid12, grid3, grid9 } from 'shared/util/grid'
import { WhenInView } from 'shared/util/in-view'
import styled from 'styled-components'
import ChannelBanner from './channel-banner'
import PaginationCarousels, { BranchGrid } from './children/pagination-carousels'
import BulkEditLauncher from './gifs/bulk-edit-launcher'
import ChannelGrid from './gifs/grid'
import ChannelHeader from './header'
import ListChannel from './list/list-channel'
import Stories from './stories'
import produce from 'immer'

const spread = produce(Object.assign)

const Container = styled.div`
    padding-top: 30px;
    display: flex;
    flex-direction: column;
    width: ${grid12}px;
`

const ContentContainer = styled.div`
    display: flex;
`

const Sidebar = styled.div`
    margin-right: gridSpacing;
    width: ${grid3}px;
`

const Content = styled.div`
    width: ${grid9}px;
`

const VideoPaginationCarousel = styled(SharedVideoPaginationCarousel)`
    margin-top: 30px;
`

type Props = {
    channelId: number
    channelType: ChannelType
    query: string
    gifs: IGif[]
    fetchChannel: () => void
    fetchGifs: () => void
    gridType: GridType
    isOwner: boolean
}
type State = {
    channel: IChannel | undefined
    cacheId: number
}
export default class RootChannel extends PureComponent<Props, State> {
    static contextType = RefreshDataContext
    state: State = {
        channel: undefined,
        cacheId: 0,
    }
    contextProps: ChannelPageContextProps
    refreshCallback: (channel: IChannel, options: RefreshChannelOptions) => void
    async componentDidMount() {
        const { channelId, channelType } = this.props

        setGTADataLayer({ dimension: `ChannelPage`, value: channelId })
        // logs for pageview
        let channel
        if (channelType === 'video_channel') {
            channel = await fetchChannel(channelId, 'videos')
        } else {
            channel = await fetchChannel(channelId)
        }
        this.setState({ channel })
        // when we save an edit, we'll refresh the view
        const { onRefreshChannel } = this.context
        this.refreshCallback = (channel, { deepRefresh }) => {
            if (channel.id === channelId) {
                const { cacheId } = this.state
                this.setState({ channel, cacheId: deepRefresh ? cacheId + 1 : cacheId })
            }
        }
        onRefreshChannel(this.refreshCallback)
    }
    componentWillUnmount() {
        const { offRefreshChannel } = this.context
        const { channel } = this.state
        if (channel) {
            offRefreshChannel(channel!.id, this.refreshCallback)
        }
    }
    render() {
        const { query, channelType, channelId, gridType, isOwner } = this.props
        const { channel, cacheId } = this.state
        if (!channel) {
            return null
        }

        assertIsDefined<IChannel>(channel)

        const {
            parent,
            screensaver_gif,
            has_children,
            user,
            is_live,
            video_channel,
            children: channelChildren,
        } = channel

        const username = user ? user.username : ''
        const isChild = !!parent
        const isSearch = !isEmpty(query)
        const hasChannel = !isEmpty(channel)
        const screensaverUrl = getUrl(screensaver_gif, 'fixed_height') || ''
        const isTagView = channelType === 'tagged_channel'
        const isBranch = isChild && has_children
        const hasVideo = video_channel && video_channel.count > 0
        this.contextProps = spread(this.contextProps || {}, {
            isOwner,
            channel,
            channelType,
            isTagView,
            searchQuery: query,
        })
        return (
            <ChannelPageContext.Provider value={this.contextProps}>
                <Container>
                    <BulkEditLauncher />
                    <ChannelBanner />
                    <ContentContainer>
                        <Sidebar>
                            <ChannelInfo
                                channel={channel}
                                isOwner={isOwner}
                                search={query}
                                isTagView={isTagView}
                                isLoading={!user}
                            />
                        </Sidebar>
                        <Content>
                            {hasChannel && <ChannelHeader />}
                            {username && !isChild && !isSearch && gridType !== 'list' && (
                                <Stories isOwner={isOwner} username={username} title={'Stories'} />
                            )}
                            {hasChannel && gridType === 'list' ? (
                                <ListChannel gridTemplate={'channel'} {...this.props} id={channelId} isLive={is_live} />
                            ) : (
                                <div key={cacheId}>
                                    {hasVideo && (
                                        <VideoPaginationCarousel
                                            title={`Videos`}
                                            channel={channel.video_channel!}
                                            titleUrl={channel.video_channel!.url}
                                            width={grid9}
                                            hideAttribution
                                        />
                                    )}
                                    {!isSearch &&
                                        !isTagView &&
                                        (isBranch ? (
                                            <BranchGrid channels={channelChildren} />
                                        ) : (
                                            <PaginationCarousels channels={channelChildren} />
                                        ))}
                                    {hasChannel && (
                                        <WhenInView dontUnmount>
                                            {channelType === 'video_channel' ? (
                                                <VideoPreviewChannelGrid
                                                    channelId={channel.id}
                                                    previewWidth={grid3}
                                                    hideAttribution
                                                />
                                            ) : (
                                                <ChannelGrid />
                                            )}
                                        </WhenInView>
                                    )}
                                </div>
                            )}
                        </Content>
                        {screensaverUrl ? <Screensaver images={[screensaverUrl]} /> : null}
                    </ContentContainer>
                </Container>
            </ChannelPageContext.Provider>
        )
    }
}
